<template>
	<div class="product-detail-loading">
		<div class="loading-back">
			<van-icon name="arrow-left" size="1.2rem" color="#666" />
		</div>

		<div class="loading-card loading-gallery">
			<van-skeleton :loading="true" :row="0">
				<template #avatar>
					<div class="gallery-avatar"></div>
				</template>
			</van-skeleton>
		</div>

		<div class="loading-card loading-summary">
			<van-skeleton :loading="true" title :row="2" row-width="100%">
				<template #title>
					<div class="summary-title"></div>
				</template>
				<template #template>
					<div class="summary-line summary-line-price"></div>
					<div class="summary-line summary-line-value"></div>
				</template>
			</van-skeleton>
		</div>

		<div class="loading-card loading-detail">
			<div v-for="item in detailRows" :key="item" class="detail-row">
				<div class="detail-icon"></div>
				<div class="detail-content">
					<van-skeleton :loading="true" title :row="4" row-width="88%">
						<template #title>
							<div class="detail-title"></div>
						</template>
						<template #template>
							<div class="detail-text"></div>
						</template>
					</van-skeleton>
				</div>
			</div>
		</div>

		<div class="loading-card loading-notice">
			<div class="notice-title"></div>
			<van-skeleton :loading="true" :row="3" row-width="100%">
				<template #template>
					<div class="notice-line"></div>
					<div class="notice-line"></div>
					<div class="notice-line short"></div>
				</template>
			</van-skeleton>
		</div>

		<div class="loading-footer">
			<div class="footer-side">
				<div class="footer-side-item" v-for="item in 3" :key="item">
					<div class="footer-side-icon"></div>
					<div class="footer-side-text"></div>
				</div>
			</div>

			<div class="footer-actions">
				<div class="footer-button footer-button-cart"></div>
				<div class="footer-button footer-button-buy"></div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
const detailRows = [1, 2, 3, 4];
</script>

<style scoped>
.product-detail-loading {
	min-height: 100vh;
	padding: 0.67rem 0.67rem 6rem;
	background: #f7f8fa;
}

.loading-back {
	display: flex;
	align-items: center;
	height: 2rem;
	padding-left: 0.08rem;
}

.loading-card {
	background: #fff;
	border-radius: 0;
	overflow: hidden;
}

.loading-gallery {
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 23rem;
	margin-bottom: 0.84rem;
}

:deep(.loading-gallery .van-skeleton) {
	width: 100%;
}

:deep(.loading-gallery .van-skeleton__avatar) {
	margin-right: 0;
}

.gallery-avatar {
	width: 80%;
	margin: 0 auto;
	height: 21rem;
	background: radial-gradient(circle at 50% 36%, rgba(180, 214, 255, 0.7) 0%, rgba(236, 243, 255, 0.9) 26%, #f7f9fc 60%),
		linear-gradient(180deg, #fbfcfe 0%, #eef3fa 100%);
}

.loading-summary {
	padding: 1.3rem 0.84rem 0.75rem;
	margin-bottom: 0.84rem;
}

.summary-title {
	width: 58%;
	height: 1.42rem;
	margin-bottom: 0.84rem;
	border-radius: 0.33rem;
	background: var(--van-skeleton-avatar-background, #f2f3f5);
}

.summary-line {
	height: 1rem;
	border-radius: 0.33rem;
	background: var(--van-skeleton-paragraph-background, #f2f3f5);
}

.summary-line-price {
	width: 28%;
	margin-bottom: 0.58rem;
}

.summary-line-value {
	width: 38%;
}

.loading-detail {
	margin-bottom: 0.84rem;
}

.detail-row {
	display: flex;
	align-items: flex-start;
	padding: 1.3rem 0.84rem;
	border-bottom: 1px solid #f2f3f5;
}

.detail-row:last-child {
	border-bottom: none;
}

.detail-icon {
	width: 1rem;
	height: 1rem;
	margin-top: 0.16rem;
	margin-right: 0.58rem;
	border-radius: 0.2rem;
	background: var(--van-skeleton-paragraph-background, #f2f3f5);
}

.detail-content {
	flex: 1;
}

.detail-title {
	width: 24%;
	height: 1.3rem;
	margin-bottom: 0.58rem;
	border-radius: 0.25rem;
	background: var(--van-skeleton-paragraph-background, #f2f3f5);
}

.detail-text {
	width: 86%;
	height: 1.3rem;
	border-radius: 0.25rem;
	background: var(--van-skeleton-paragraph-background, #f2f3f5);
}

.loading-notice {
	padding: 1.3rem 0.84rem 1rem;
}

.notice-title {
	width: 22%;
	height: 1.4rem;
	margin-bottom: 0.84rem;
	border-radius: 0.25rem;
	background: var(--van-skeleton-paragraph-background, #f2f3f5);
}

.notice-line {
	width: 100%;
	height: 1.3rem;
	margin-bottom: 0.58rem;
	border-radius: 0.25rem;
	background: var(--van-skeleton-paragraph-background, #f2f3f5);
}

.notice-line.short {
	width: 72%;
	margin-bottom: 0;
}

.loading-footer {
	position: fixed;
	left: 0;
	right: 0;
	bottom: 0;
	display: flex;
	align-items: stretch;
	justify-content: space-between;
	padding: 0.42rem 0.67rem calc(0.42rem + env(safe-area-inset-bottom));
	background: #fff;
	box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.06);
}

.footer-side {
	display: flex;
	align-items: center;
	flex: 1;
}

.footer-side-item {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	width: 2.9rem;
	margin-right: 0.2rem;
}

.footer-side-icon {
	width: 1.08rem;
	height: 1.08rem;
	margin-bottom: 0.25rem;
	border-radius: 0.25rem;
	background: var(--van-skeleton-paragraph-background, #f2f3f5);
}

.footer-side-text {
	width: 1.5rem;
	height: 0.58rem;
	border-radius: 0.2rem;
	background: var(--van-skeleton-paragraph-background, #f2f3f5);
}

.footer-actions {
	display: flex;
	align-items: center;
}

.footer-button {
	width: 5.67rem;
	height: 2.75rem;
	border-radius: 0;
}

.footer-button-cart {
	background: linear-gradient(180deg, #ff7f63 0%, #ff5a36 100%);
}

.footer-button-buy {
	background: linear-gradient(180deg, #ff5a5a 0%, #e83232 100%);
}
</style>
